import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  FormControl,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { Add } from '@mui/icons-material'
import omit from 'lodash/omit'
import { LoadingButton } from '@mui/lab'
import { CatalogProductStatus } from '../enums'
import { usePolicy } from '../contexts/PolicyContext'
import uploadService from '../services/uploadService'
import catalogProductService from '../services/catalogProductService'
import CatalogProductManufacturerAutocomplete from './CatalogProductManufacturerAutocomplete'
import CatalogProductProductCodeAutocomplete from './CatalogProductProductCodeAutocomplete'
import Dropzone from './Dropzone'
import CatalogProductAutocomplete from './CatalogProductAutocomplete'
import CatalogProductTypeSelect from './CatalogProductTypeSelect'
import CatalogProductStatusSelect from './CatalogProductStatusSelect'
import { useTranslation } from 'react-i18next'
import { CatalogProductType } from '../enums/CatalogProductType'
import { CatalogProduct } from '../types'
import { useCatalogProductForm } from '../hooks/catalogProduct'
import { ElectricalCabinetCatalogProductCustomDataFields } from './ElectricalCabinetCatalogProductForm'
import { SpineModuleCatalogProductCustomDataFields } from './SpineModuleCatalogProductForm'
import { SpineLoadCatalogProductCustomDataFields } from './SpineLoadCatalogProductForm'
import { SpineMultimeasurementCatalogProductCustomDataFields } from './SpineMultimeasurementCatalogProductForm'
import { HeaterCatalogProductCustomDataFields } from './HeaterCatalogProductForm'
import { GeneratorCatalogProductCustomDataFields } from './GeneratorCatalogProductForm'
import { AuroraCatalogProductCustomDataFields } from './AuroraCatalogProductForm'
import { AxiomaW1CatalogProductCustomDataFields } from './AxiomaW1CatalogProductForm'
import { ValveCatalogProductCustomDataFields } from './ValveCatalogProductForm'
import NominalCurrentSelect from './NominalCurrentSelect'
import ClampRatioField from './manufacturing/ClampRatioField'
import InputCablesSelect from './InputCablesSelect'
import SinglePhaseToggleButtonGroup from './SinglePhaseToggleButtonGroup'
import { getClampRatioForNominalCurrent } from '../utils/powerSupply'
import { isElectricalCabinetCatalogProduct } from '../utils/catalogProduct'
import { ElectricalCabinetCatalogProduct } from '../types/CatalogProduct'
import {
  AuroraCatalogProductCustomDataInput,
  AxiomaW1CatalogProductCustomDataInput,
  ElectricalCabinetCatalogProductCustomDataInput,
  GeneratorCatalogProductCustomDataInput,
  HeaterCatalogProductCustomDataInput,
  SpineLoadCatalogProductCustomDataInput,
  SpineModuleCatalogProductCustomDataInput,
  SpineMultimeasurementCatalogProductCustomDataInput,
  ValveCatalogProductCustomDataInput
} from '../types/CatalogProductInput'

export interface CatalogProductFormProps {
  type?: CatalogProductType
  catalogProduct?: CatalogProduct
  onCancel?: () => void
  onError?: (error: unknown) => void
  afterSubmit?: (catalogProduct: CatalogProduct) => void
}

/**
 * The catalog product form. Renders the form of the given type, or of the catalog product's type,
 * and lets the user select the type when neither is known.
 */
export default function CatalogProductForm({
  type,
  catalogProduct,
  onCancel,
  onError,
  afterSubmit
}: CatalogProductFormProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('catalogProduct')

  /**
   * The catalog product type, if known. Selected by the user when not known beforehand.
   */
  const [resolvedType, setResolvedType] = useState<CatalogProductType | undefined>(
    catalogProduct?.type ?? type
  )

  /**
   * Whether the user has to select the type.
   */
  const isTypeSelectable = catalogProduct === undefined && type === undefined

  const form = useCatalogProductForm({
    type: resolvedType,
    catalogProduct,
    afterSubmit,
    afterError: onError
  })

  const { isSuperadmin, isManufacturer } = usePolicy()

  const { values, isSubmitting, isError, getHelperText, handleBlur, handleChange } = form

  /**
   * The preview of the selected or current photo.
   */
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null)

  /**
   * Heaters also suggest the product codes of verified heaters.
   */
  const isHeater = resolvedType === CatalogProductType.HEATER

  /**
   * Suggest the manufacturers of verified catalog products of the same type.
   */
  const manufacturerQuery = useMemo(
    () => ({ type: resolvedType, status: CatalogProductStatus.VERIFIED }),
    [resolvedType]
  )

  function handleAutocompleteChange(field: string, value: string | null) {
    form.setFieldValue(field, value ?? '')
    form.setFieldTouched(field, true, false)
  }

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null

    if (form.photo !== null) {
      objectUrl = URL.createObjectURL(form.photo)
      setPreviewImageUrl(objectUrl)
    } else if (!catalogProduct?.photo?.id) {
      setPreviewImageUrl(null)
    } else {
      uploadService
        .getUpload(catalogProduct.photo.id)
        .then((result) => {
          if (!cancelled) {
            setPreviewImageUrl(result.url)
          }
        })
        .catch(() => {
          if (!cancelled) {
            setPreviewImageUrl(null)
          }
        })
    }

    return () => {
      cancelled = true

      if (objectUrl !== null) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [catalogProduct?.photo?.id, form.photo])

  /**
   * Select the type. The form resets to the initial values of the type.
   */
  function handleTypeChange(nextType: CatalogProductType) {
    setResolvedType(nextType)
  }

  return (
    <Stack spacing={6}>
      {isTypeSelectable && (
        <FormControl fullWidth>
          <InputLabel id="catalog-product-type-label">{t('type')}</InputLabel>
          <CatalogProductTypeSelect
            name="type"
            labelId="catalog-product-type-label"
            label={t('type')}
            value={resolvedType ?? ''}
            onChange={handleTypeChange}
          />
        </FormControl>
      )}

      {resolvedType !== undefined ? (
        <form onSubmit={form.handleSubmit}>
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label={t('name')}
                value={values.name}
                error={isError('name')}
                helperText={getHelperText('name')}
                disabled={isSubmitting}
                variant="outlined"
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <CatalogProductManufacturerAutocomplete
                query={manufacturerQuery}
                value={values.manufacturer}
                label={t('manufacturer')}
                error={isError('manufacturer')}
                helperText={getHelperText('manufacturer')}
                disabled={isSubmitting}
                fullWidth
                onBlur={handleBlur}
                onChange={(_, selectedValue) =>
                  handleAutocompleteChange('manufacturer', selectedValue)
                }
                onInputChange={(_, inputValue) =>
                  handleAutocompleteChange('manufacturer', inputValue)
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {isHeater ? (
                <CatalogProductProductCodeAutocomplete
                  query={manufacturerQuery}
                  value={values.manufacturerProductCode}
                  label={t('manufacturerProductCode')}
                  error={isError('manufacturerProductCode')}
                  helperText={getHelperText('manufacturerProductCode')}
                  disabled={isSubmitting}
                  fullWidth
                  onBlur={handleBlur}
                  onChange={(_, selectedValue) =>
                    handleAutocompleteChange('manufacturerProductCode', selectedValue)
                  }
                  onInputChange={(_, inputValue) =>
                    handleAutocompleteChange('manufacturerProductCode', inputValue)
                  }
                />
              ) : (
                <TextField
                  name="manufacturerProductCode"
                  label={t('manufacturerProductCode')}
                  value={values.manufacturerProductCode}
                  error={isError('manufacturerProductCode')}
                  helperText={getHelperText('manufacturerProductCode')}
                  disabled={isSubmitting}
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                multiline
                rows={4}
                name="description"
                label={t('description')}
                value={values.description}
                error={isError('description')}
                helperText={getHelperText('description')}
                disabled={isSubmitting}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            {(isSuperadmin || isManufacturer) && (
              <Grid item xs={12}>
                <FormControl fullWidth error={isError('status')} disabled={isSubmitting}>
                  <InputLabel id="catalog-product-status-label">{t('status')}</InputLabel>
                  <CatalogProductStatusSelect
                    name="status"
                    labelId="catalog-product-status-label"
                    label={t('status')}
                    value={values.status}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  <FormHelperText>{getHelperText('status')}</FormHelperText>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <Dropzone
                accept={{ 'image/*': [] }}
                minHeight={120}
                height="auto"
                previewImageUrl={previewImageUrl}
                buttonText={t('selectPhotoFromYourDevice')}
                previewAlt="Catalog product photo"
                selectedFileName={form.photo?.name}
                onDrop={(acceptedFiles) => form.setPhoto(acceptedFiles[0] ?? null)}
              />
            </Grid>

            <Grid item xs={12}>
              {resolvedType === CatalogProductType.ELECTRICAL_CABINET && (
                <ElectricalCabinetCatalogProductCustomDataFields
                  value={form.values.customData as ElectricalCabinetCatalogProductCustomDataInput}
                  catalogProduct={catalogProduct}
                  disabled={form.isSubmitting}
                  isError={form.isError}
                  getHelperText={form.getHelperText}
                  onChange={(customData) => form.setFieldValue('customData', customData)}
                />
              )}

              {resolvedType === CatalogProductType.SPINE_MODULE && (
                <SpineModuleCatalogProductCustomDataFields
                  value={form.values.customData as SpineModuleCatalogProductCustomDataInput}
                  catalogProduct={catalogProduct}
                  disabled={form.isSubmitting}
                  isError={form.isError}
                  getHelperText={form.getHelperText}
                  onChange={(customData) => form.setFieldValue('customData', customData)}
                />
              )}

              {resolvedType === CatalogProductType.SPINE_LOAD && (
                <SpineLoadCatalogProductCustomDataFields
                  value={form.values.customData as SpineLoadCatalogProductCustomDataInput}
                  catalogProduct={catalogProduct}
                  disabled={form.isSubmitting}
                  isError={form.isError}
                  getHelperText={form.getHelperText}
                  onChange={(customData) => form.setFieldValue('customData', customData)}
                />
              )}

              {resolvedType === CatalogProductType.SPINE_MULTI_MEASUREMENT && (
                <SpineMultimeasurementCatalogProductCustomDataFields
                  value={
                    form.values.customData as SpineMultimeasurementCatalogProductCustomDataInput
                  }
                  catalogProduct={catalogProduct}
                  disabled={form.isSubmitting}
                  isError={form.isError}
                  getHelperText={form.getHelperText}
                  onChange={(customData) => form.setFieldValue('customData', customData)}
                />
              )}

              {resolvedType === CatalogProductType.HEATER && (
                <HeaterCatalogProductCustomDataFields
                  value={form.values.customData as HeaterCatalogProductCustomDataInput}
                  catalogProduct={catalogProduct}
                  disabled={form.isSubmitting}
                  isError={form.isError}
                  getHelperText={form.getHelperText}
                  onChange={(customData) => form.setFieldValue('customData', customData)}
                />
              )}

              {resolvedType === CatalogProductType.GENERATOR && (
                <GeneratorCatalogProductCustomDataFields
                  value={form.values.customData as GeneratorCatalogProductCustomDataInput}
                  catalogProduct={catalogProduct}
                  disabled={form.isSubmitting}
                  isError={form.isError}
                  getHelperText={form.getHelperText}
                  onChange={(customData) => form.setFieldValue('customData', customData)}
                />
              )}

              {resolvedType === CatalogProductType.AURORA && (
                <AuroraCatalogProductCustomDataFields
                  value={form.values.customData as AuroraCatalogProductCustomDataInput}
                  catalogProduct={catalogProduct}
                  disabled={form.isSubmitting}
                  isError={form.isError}
                  getHelperText={form.getHelperText}
                  onChange={(customData) => form.setFieldValue('customData', customData)}
                />
              )}

              {resolvedType === CatalogProductType.AXIOMA_W1 && (
                <AxiomaW1CatalogProductCustomDataFields
                  value={form.values.customData as AxiomaW1CatalogProductCustomDataInput}
                  catalogProduct={catalogProduct}
                  disabled={form.isSubmitting}
                  isError={form.isError}
                  getHelperText={form.getHelperText}
                  onChange={(customData) => form.setFieldValue('customData', customData)}
                />
              )}

              {resolvedType === CatalogProductType.VALVE && (
                <ValveCatalogProductCustomDataFields
                  value={form.values.customData as ValveCatalogProductCustomDataInput}
                  catalogProduct={catalogProduct}
                  disabled={form.isSubmitting}
                  isError={form.isError}
                  getHelperText={form.getHelperText}
                  onChange={(customData) => form.setFieldValue('customData', customData)}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={3}>
                <Button variant="text" color="inherit" disabled={isSubmitting} onClick={onCancel}>
                  {t('actions:cancel')}
                </Button>

                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                >
                  {t('actions:save')}
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </form>
      ) : (
        <Stack direction="row" justifyContent="flex-end">
          <Button variant="text" color="inherit" onClick={onCancel}>
            {t('actions:cancel')}
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

export interface SpineConfigFieldsProps<TValue extends SpineModuleCatalogProductCustomDataInput> {
  value: TValue
  disabled?: boolean
  electricalCabinet?: ElectricalCabinetCatalogProduct | null
  onChange: (value: TValue) => void
}

export function SpineConfigFields<TValue extends SpineModuleCatalogProductCustomDataInput>({
  value,
  disabled = false,
  electricalCabinet = null,
  onChange
}: SpineConfigFieldsProps<TValue>) {
  const { t } = useTranslation('catalogProduct')

  const isMaxCurrentDisabled =
    disabled ||
    (electricalCabinet?.customData?.nominalCurrent ?? electricalCabinet?.customData?.fuse) !==
      undefined

  const isInputCablesDisabled = disabled || electricalCabinet?.customData?.inputCables !== undefined

  function handleMaxCurrentChange(maxCurrent: number) {
    onChange({
      ...value,
      spine_config: {
        ...value.spine_config,
        max_current: maxCurrent,
        clamp_ratio: getClampRatioForNominalCurrent(maxCurrent)
      }
    })
  }

  function handleClampRatioChange(clampRatio: number | null) {
    onChange({
      ...value,
      spine_config: { ...value.spine_config, clamp_ratio: clampRatio ?? undefined }
    })
  }

  function handleInputCablesChange(inputCables: number | undefined) {
    onChange({ ...value, spine_config: { ...value.spine_config, input_cables: inputCables } })
  }

  function handleSinglePhaseChange(singlePhase: 0 | 1 | null) {
    onChange({
      ...value,
      spine_config:
        singlePhase === null
          ? omit(value.spine_config, 'single_phase')
          : { ...value.spine_config, single_phase: singlePhase }
    })
  }

  return (
    <FormGroup>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {t('powerSupply')}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <NominalCurrentSelect
            value={value.spine_config?.max_current}
            disabled={isMaxCurrentDisabled}
            fullWidth
            onChange={handleMaxCurrentChange}
          />
        </Grid>

        <Grid item xs={12}>
          <ClampRatioField
            value={value.spine_config?.clamp_ratio ?? null}
            disabled={disabled}
            fullWidth
            allowEmpty
            onValueChange={handleClampRatioChange}
          />
        </Grid>

        <Grid item xs={12}>
          <InputCablesSelect
            value={value.spine_config?.input_cables}
            disabled={isInputCablesDisabled}
            fullWidth
            onChange={handleInputCablesChange}
          />
        </Grid>

        <Grid item xs={12}>
          <SinglePhaseToggleButtonGroup
            value={value.spine_config?.single_phase}
            disabled={disabled}
            fullWidth
            onChange={handleSinglePhaseChange}
          />
        </Grid>
      </Grid>
    </FormGroup>
  )
}

type ElectricalCabinetFieldsValue = SpineModuleCatalogProductCustomDataInput & {
  electricalCabinet?: { catalogProductId?: string }
}

export interface ElectricalCabinetFieldsProps<TValue extends ElectricalCabinetFieldsValue> {
  value: TValue
  disabled?: boolean
  onChange: (value: TValue) => void
  onElectricalCabinetChange: (electricalCabinet: ElectricalCabinetCatalogProduct | null) => void
}

/**
 * Links an electrical cabinet, and maps its settings to the spine config.
 */
export function ElectricalCabinetFields<TValue extends ElectricalCabinetFieldsValue>({
  value,
  disabled = false,
  onChange,
  onElectricalCabinetChange
}: ElectricalCabinetFieldsProps<TValue>) {
  const { t } = useTranslation('catalogProduct')

  function handleElectricalCabinetChange(selected: CatalogProduct | null) {
    const electricalCabinet =
      selected !== null && isElectricalCabinetCatalogProduct(selected) ? selected : null

    onElectricalCabinetChange(electricalCabinet)

    if (selected === null) {
      onChange(omit(value, 'electricalCabinet') as TValue)
      return
    }

    const customData = electricalCabinet?.customData

    const maxCurrent = customData?.nominalCurrent ?? customData?.fuse

    onChange({
      ...value,
      spine_config: {
        ...value.spine_config,
        ...(maxCurrent !== undefined && {
          max_current: maxCurrent,
          clamp_ratio: customData?.clampRatio ?? getClampRatioForNominalCurrent(maxCurrent)
        }),
        ...(customData?.inputCables !== undefined && { input_cables: customData.inputCables })
      },
      electricalCabinet: { catalogProductId: selected.id }
    })
  }

  function filterByMaxCurrent(catalogProduct: CatalogProduct) {
    const maxCurrent = value.spine_config?.max_current

    if (
      maxCurrent === undefined ||
      catalogProduct.id === value.electricalCabinet?.catalogProductId
    ) {
      return true
    }

    return (
      isElectricalCabinetCatalogProduct(catalogProduct) &&
      (catalogProduct.customData?.nominalCurrent ?? catalogProduct.customData?.fuse) === maxCurrent
    )
  }

  return (
    <FormGroup>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {t('electricalCabinet')}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <CatalogProductSelectField
            type={CatalogProductType.ELECTRICAL_CABINET}
            value={value.electricalCabinet?.catalogProductId ?? null}
            disabled={disabled}
            filterKey={value.spine_config?.max_current}
            filterBy={filterByMaxCurrent}
            onChange={handleElectricalCabinetChange}
          />
        </Grid>
      </Grid>
    </FormGroup>
  )
}

export interface CatalogProductSelectFieldProps {
  type: CatalogProductType
  value: string | null
  disabled: boolean
  filterBy?: (catalogProduct: CatalogProduct) => boolean
  filterKey?: string | number
  onChange: (catalogProduct: CatalogProduct | null) => void
}

export function CatalogProductSelectField({
  type,
  value,
  disabled,
  filterBy,
  filterKey,
  onChange
}: CatalogProductSelectFieldProps) {
  const { t } = useTranslation('catalogProduct')

  const [isSelecting, setIsSelecting] = useState<boolean>(false)

  /**
   * The catalog product of the value.
   */
  const [catalogProduct, setCatalogProduct] = useState<CatalogProduct | null>(null)

  /**
   * Load the catalog product and emit it as the selected one.
   */
  async function loadCatalogProduct(catalogProductId: string, controller?: AbortController) {
    try {
      const loadedCatalogProduct = await catalogProductService.getCatalogProduct(catalogProductId, {
        signal: controller?.signal
      })

      setCatalogProduct(loadedCatalogProduct)
      onChange(loadedCatalogProduct)
    } catch {
      // Ignore the error.
    }
  }

  function handleChange(selected: CatalogProduct | null) {
    setCatalogProduct(selected)
    onChange(selected)
  }

  /**
   * Load the catalog product of the value when it is not known.
   */
  useEffect(() => {
    if (value === null || catalogProduct?.id === value) {
      return
    }

    const controller = new AbortController()

    loadCatalogProduct(value, controller)

    return () => controller.abort()
  }, [value])

  if (!isSelecting && !value) {
    return (
      <Button
        variant="outlined"
        startIcon={<Add />}
        disabled={disabled}
        onClick={() => setIsSelecting(true)}
      >
        {t('addCatalogProduct')}
      </Button>
    )
  }

  return (
    <CatalogProductAutocomplete
      key={filterKey}
      type={type}
      filterBy={filterBy}
      label={t('catalogProduct')}
      value={value}
      disabled={disabled}
      onChange={(_, selected) => handleChange(selected)}
    />
  )
}
